import React, { useState } from 'react';
import { NotificationItem, updateNotificationVisibility } from '../services/notification-service';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
// import { useCommonStore } from 'state/store/common/useCommonStore'
import { CheckCircle2 } from 'lucide-react';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { useNotificationFeedStore } from 'state/store/notifications/use-notification-feed-store';
import { useToast } from 'hooks/use-toast';
import { NotificationType } from 'features/chat/types/notification-types';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { useTheme } from 'components/core/theme-provider';
import { Button } from 'components/ui/button';
import { useNavigate } from 'react-router-dom';
import { resolveNotificationText } from '../utils/resolve-notification-text';



function GroupNotification({ notification, onClose }: { notification: NotificationItem, onClose: () => void }) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [isMarkingAsRead, setIsMarkingAsRead] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const { theme } = useTheme();
    const navigate = useNavigate();

    const { fetchAndSetNotification, currentPage } = useNotificationFeedStore();
    const sidebarConversationsList = useChatStore((state) => state.sidebarConversationsList);


    const formatTimestamp = (timestamp: string) => {
        try {
            return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
        } catch {
            return timestamp;
        }
    };

    const activity = notification.activity?.verb;
    // console.log('activity', activity);

    const getNotificationConfig = () => {
        const translationKey = notification?.activity?.extraContext?.translationKey;
        const notificationType = notification.activity?.object?.type;
        const displayName = notification?.activity?.object?.displayName;

        const buildMessage = (params: Record<string, string>, fallback: string): string => {
            return t(translationKey || '', params) || fallback;
        };

        // Get conversation name for COLLABORATION type
        const getConversationName = (): string => {
            if (notificationType !== NotificationType.COLLABORATION) return '';
            const conversationId = notification?.activity?.object?.objectId;
            const conversation = sidebarConversationsList.find(
                (item) => item.ConversationId === conversationId
            );
            return conversation?.ConversationTitle || 'Conversation';
        };

        if (activity === 'ADD') {
            if (notificationType === NotificationType.GROUP || notificationType === NotificationType.ORGANISATION) {
                return {
                    message: buildMessage(
                        { organizationName: `${displayName}` },
                        `You were added to ${displayName}`
                    ),
                };
            }
            if (notificationType === NotificationType.COLLABORATION) {
                const conversationId = notification?.activity?.object?.objectId;
                const conversationName = getConversationName();
                return {
                    message: buildMessage(
                        { conversationName: `${conversationName}` },
                        `You were added to the chat ${conversationName}`
                    ),
                    conversationId,
                };
            }

            if (notificationType === NotificationType.PLAN) {
                return {
                    message: buildMessage(
                        { planName: `${displayName}` },
                        `Your plan was upgraded to ${displayName}`
                    ),
                };
            }

            else if (notificationType === NotificationType.LOGIN) {

                const browserName = notification?.activity?.extraContext?.browser;
                const deviceName = notification?.activity?.extraContext?.device;

                return {
                    message: buildMessage(
                        { browserName: `${browserName}`, deviceName: `${deviceName}` },
                        `New login detected from ${browserName} on ${deviceName}.`
                    ),
                };
            }
            else if (notificationType === NotificationType.ROLE) {
                const organizationName = notification?.activity?.target?.displayName;

                return {
                    message: buildMessage(
                        { organizationName: `${organizationName}` },
                        `You were granted admin privileges for the organization ${organizationName}`
                    ),
                };
            }
        } else if (activity === 'REMOVE') {
            if (notificationType === NotificationType.GROUP || notificationType === NotificationType.ORGANISATION) {
                return {
                    message: buildMessage(
                        { organizationName: `${displayName}` },
                        `You were removed from ${displayName}`
                    ),
                };
            }
            if (notificationType === NotificationType.COLLABORATION) {
                const conversationName = getConversationName();
                return {
                    message: buildMessage(
                        { conversationName: `${conversationName}` },
                        `You were removed from the chat ${conversationName}`
                    ),
                };
            }
            if (notificationType === NotificationType.ROLE) {
                const organizationName = notification?.activity?.extraContext?.organizationName;
                return {
                    message: buildMessage(
                        { organizationName: `${organizationName}` },
                        `You were removed from admin privileges for ${organizationName}`
                    ),
                };
            }
            else if (notificationType === NotificationType.SUBSCRIPTION) {
                return {
                    message: buildMessage(
                        {},
                        'Your subscription has expired. Renew to continue using SeliseAI.'
                    ),
                };
            }
            else if (notificationType === NotificationType.PLAN) {
                return {
                    message: buildMessage(
                        { planName: `${displayName}` },
                        `Your plan was downgraded to ${displayName}`
                    ),
                };
            }
        }
        else if (activity === 'SHARE') {
            if (notificationType === NotificationType.FILE) {
                return {
                    message: buildMessage(
                        { fileName: `${displayName}` },
                        `A file ${displayName} was shared with you`
                    ),
                };
            }
        }

        else if (activity === 'CHANGE') {

            if (notificationType === NotificationType.SUBSCRIPTION) {
                return {
                    message: buildMessage(
                        { planName: `${displayName}` },
                        `Your subscription plan was upgraded to ${displayName}`
                    ),
                };
            }
            else if (notificationType === NotificationType.PASSWORD) {
                return {
                    message: buildMessage(
                        { password: `${displayName}` },
                        `Your password was successfully changed.`
                    ),
                };
            }

        }

        else if (activity === 'Share') {

            if (notificationType === NotificationType.FILE) {
                return {
                    message: buildMessage(
                        { planName: `${displayName}` },
                        `Your subscription plan was upgraded to ${displayName}`
                    ),
                };
            }


        }
        else if (activity === 'ACCEPT' && notificationType === NotificationType.SUBSCRIPTION) {
            return {
                message: buildMessage(
                    { planName: `${displayName}` },
                    `Your payment for ${displayName} was successful.`
                ),
            };
        }

        else if (activity === 'FAILED' && notificationType === NotificationType.SUBSCRIPTION) {
            return {
                message: buildMessage(
                    { planName: `${displayName}` },
                    `Your payment for ${displayName} failed.`
                ),
            };
        }

        else if (activity === 'CANCEL' && notificationType === NotificationType.SUBSCRIPTION) {
            return {
                message: buildMessage(
                    {},
                    'Your subscription was cancelled.'
                ),
            };
        }

        // Standard Blocks-OS / Genesis denormalizedPayload support
        if (notification.denormalizedPayload) {
            try {
                const parsed = typeof notification.denormalizedPayload === 'string'
                    ? JSON.parse(notification.denormalizedPayload)
                    : notification.denormalizedPayload;

                if (parsed?.Message) {
                    const desc = parsed.Message.description || parsed.Message.title;
                    const displayName = parsed.Message.displayName || '';
                    if (desc) {
                        return { message: t(desc, { organizationName: displayName }) || desc };
                    }
                }

                const { title, description } = resolveNotificationText(parsed, t);
                if (title || description) {
                    return {
                        message: title && description ? `${title}: ${description}` : title || description,
                        // No title: the description heads the item, as a title-only item's title does today.
                        title: title || description,
                    };
                }
            } catch {
                // fallback
            }
        }

        // Direct message / description / title fields
        const directText = notification.message || notification.description || notification.title;
        if (directText) {
            return { message: t(directText) || directText, title: notification.title || '' };
        }

        return {
            message: buildMessage({}, 'Notification'),
        };
    };

    const config = getNotificationConfig();
    const isRead = Boolean(notification.isRead ?? notification.read);
    const isUnread = !isRead;

    const handleMarkAsRead = async (e: React.MouseEvent) => {
        e.stopPropagation();

        if (isMarkingAsRead || isRead) {
            return;
        }

        setIsMarkingAsRead(true);
        try {
            const configurationSettings = useConfigStore.getState().configurationSettings;
            if (!configurationSettings?.blocksKey) {
                throw new Error('Configuration settings or blocksKey is not available');
            }

            const itemId = notification.itemId || notification.id;
            if (!itemId) {
                throw new Error('Notification itemId is not available');
            }

            const payload = {
                projectKey: configurationSettings.blocksKey,
                itemIds: [itemId],
                seen: true,
                read: true,
            };

            const response = await updateNotificationVisibility(configurationSettings, payload);

            if (response.isSuccess) {
                // Refresh notifications to update the UI - stay on current page
                await fetchAndSetNotification(currentPage);
            } else {
                throw new Error('Failed to mark notification as read');
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error marking notification as read:', error);
            toast({
                variant: 'destructive',
                title: t('ERROR') || 'Error',
                description: t('FAILED_TO_MARK_AS_READ') || 'Failed to mark notification as read',
            });
        } finally {
            setIsMarkingAsRead(false);
        }
    };

    return (
        <div
            key={notification.itemId}
            className={`
                group cursor-pointer relative p-3 rounded-lg transition-all duration-200
                ${
                    isRead
                        ? 'bg-neutral-100/60 dark:bg-neutral-900/40 border border-neutral-200/50 dark:border-neutral-800/50 opacity-70 hover:opacity-95 hover:bg-neutral-100/80 dark:hover:bg-neutral-900/60'
                        : 'bg-blue-50/80 dark:bg-blue-950/30 border-l-[3px] border-l-blue-600 dark:border-l-blue-500 border-y border-r border-blue-100 dark:border-blue-900/40 shadow-xs'
                }
            `}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                        {isUnread && (
                            <span
                                className="w-2 h-2 rounded-full bg-blue-600 dark:bg-blue-400 shrink-0 inline-block"
                                aria-label="Unread"
                            />
                        )}
                        <h3
                            className={`text-sm truncate ${
                                isUnread
                                    ? 'font-semibold text-neutral-900 dark:text-neutral-100'
                                    : 'font-medium text-neutral-500 dark:text-neutral-400'
                            }`}
                        >
                            {t(notification.activity?.object?.type) ||
                                notification.activity?.object?.type ||
                                (config as any)?.title ||
                                t('NOTIFICATION') ||
                                'Notification'}
                        </h3>
                    </div>
                    <div className="w-6 h-6 flex items-center justify-center shrink-0">
                        {isUnread && (
                            <button
                                onClick={handleMarkAsRead}
                                disabled={isMarkingAsRead}
                                className="flex items-center justify-center p-1 rounded-full text-neutral-400 hover:text-blue-600 dark:text-neutral-400 dark:hover:text-blue-400 hover:bg-neutral-200/60 dark:hover:bg-neutral-800/60 transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100"
                                aria-label={t('MARK_AS_READ') || 'Mark as read'}
                                title={t('MARK_AS_READ') || 'Mark as read'}
                            >
                                <CheckCircle2
                                    className={`w-4 h-4 text-blue-600 dark:text-blue-400 ${
                                        isMarkingAsRead ? 'opacity-50 animate-pulse' : ''
                                    }`}
                                />
                            </button>
                        )}
                    </div>
                </div>

                <p
                    className={`text-xs ${
                        isUnread
                            ? 'font-normal text-neutral-800 dark:text-neutral-200'
                            : 'font-normal text-neutral-500 dark:text-neutral-400'
                    }`}
                >
                    {config?.message}
                </p>

                {config?.conversationId && (
                    <Button
                        variant="link"
                        size="sm"
                        className="p-0 h-auto text-xs font-medium text-blue-600 dark:text-blue-400"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                            navigate(`/chat/${config.conversationId}`);
                        }}
                    >
                        {t('GO_TO_CHAT') || 'Go to chat'}
                    </Button>
                )}

                <p
                    className={`text-xs ${
                        isUnread
                            ? 'text-blue-600/80 dark:text-blue-400/80 font-medium'
                            : 'text-neutral-400 dark:text-neutral-500'
                    }`}
                >
                    {formatTimestamp(
                        notification.timestamp || notification.createdDate || notification.createdTime || ''
                    )}
                </p>
            </div>
        </div>
    );
}

export default GroupNotification


